import { EyeIcon, EyeSlashIcon, GearSixIcon, PlusIcon } from '@phosphor-icons/react'
import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import { GET_MAINTENANCE_REQUESTS_BASE_QUERY_KEY } from '@/api/maintenance/get-maintenance-requests'
import { MaintenanceSettings } from '@/components/business/maintenance-settings'
import { Page } from '@/components/layout/page'
import { PageHeader } from '@/components/layout/page-header'
import RequestMaintenanceForm from '@/components/Maintenance/RequestMaintenanceForm/RequestMaintenanceForm'
import ModalContainer from '@/components/ModalContainer/ModelContainer'
import { Button } from '@/components/ui/button'
import { Icon } from '@/components/ui/icon'
import { ToggleSwitch } from '@/components/ui/toggle-switch'
import { popupFactory } from '@/factories'
import { useDesignVersionToggle, useLocalStorage } from '@/hooks'
import { MaintenanceDashboard } from '@/pages/maintenance/maintenance-dashboard'
import { MaintenanceDataReports } from '@/pages/maintenance/maintenance-requests/maintenance-data-reports'
import { selectActiveBuilding } from '@/reducers/buildingSelectorReducer'
import { selectRequest } from '@/reducers/maintenanceReducer'
import { selectUserRole } from '@/reducers/userDataReducer'

const MAINTENANCE_ANALYTICS_STORAGE_KEY = 'maintenance-analytics-open'
// Note: narrower than `ADMIN_OR_PM` on purpose - the org-level `Manager` role doesn't get settings.
const MAINTENANCE_SETTINGS_ROLES = ['Administrator', 'PropertyManager']

// Note: standalone new-design page (no shared toolbar/tabs, no Staff tab - see EL-14186 rework).
// Data Reports and the "selected request" preview swap stay scoped to this page only, matching
// Announcements/Surveys' analytics-hidden-near-title convention. The reusable `MaintenanceDashboard`
// (table + filters) handles its own preview swap internally so it keeps working the same way when
// embedded in the Resident Profile, which never renders this page (or Data Reports, which is
// building-wide and wouldn't make sense scoped to one resident).
export const MaintenanceRequestsPage = () => {
  const { t } = useTranslation(['maintenance'])
  const queryClient = useQueryClient()
  const selectedBuilding = useSelector(selectActiveBuilding)
  const selectedRequest = useSelector(selectRequest)
  const userRole = useSelector(selectUserRole)

  const buildingId = selectedBuilding?.id
  const canManageSettings =
    !!buildingId && !!userRole && MAINTENANCE_SETTINGS_ROLES.includes(userRole)

  const [isShowAddRequest, setShowAddRequest] = useState(false)
  const [isAnalyticsOpen, setIsAnalyticsOpen] = useLocalStorage(
    MAINTENANCE_ANALYTICS_STORAGE_KEY,
    false,
  )

  const { redirect, toggle: handleUIToggle } = useDesignVersionToggle({
    group: 'maintenance',
    screen: 'MaintenanceRequests',
    to: {
      params: { buildingId: selectedBuilding ? String(selectedBuilding.id) : '' },
      path: '/dashboard/building-dashboard/maintenance/legacy/:buildingId',
    },
    version: 'new',
  })

  const handleAddRequestSuccess = () => {
    void queryClient.invalidateQueries({ queryKey: [GET_MAINTENANCE_REQUESTS_BASE_QUERY_KEY] })
  }

  const openSettings = () => {
    if (!buildingId) return

    popupFactory.open(MaintenanceSettings, { buildingId })
  }

  if (redirect) return redirect

  return (
    <Page fillHeight>
      {!selectedRequest && (
        <>
          <PageHeader
            title={t('maintenance:page.title')}
            titleExtra={
              <Button
                size="small"
                use="secondary"
                onClick={() => setIsAnalyticsOpen(!isAnalyticsOpen)}
              >
                <Icon icon={isAnalyticsOpen ? EyeSlashIcon : EyeIcon} />
                {isAnalyticsOpen
                  ? t('maintenance:page.hideAnalytics')
                  : t('maintenance:page.showAnalytics')}
              </Button>
            }
            actions={
              <>
                <ToggleSwitch
                  falsyLabel={t('maintenance:page.designToggle.old')}
                  truthyLabel={t('maintenance:page.designToggle.new')}
                  checked={true}
                  onChange={handleUIToggle}
                />
                {canManageSettings && (
                  <Button size="small" use="secondary" onClick={openSettings}>
                    <Icon icon={GearSixIcon} />
                    {t('maintenance:page.settings')}
                  </Button>
                )}
                <Button size="small" onClick={() => setShowAddRequest(true)}>
                  {t('maintenance:toolbar.addRequest')}
                  <Icon icon={PlusIcon} />
                </Button>
              </>
            }
          />
          {isAnalyticsOpen && <MaintenanceDataReports buildingId={selectedBuilding?.id} />}
        </>
      )}
      <MaintenanceDashboard />
      {isShowAddRequest && (
        <ModalContainer
          headerTitle={t('maintenance:page.createRequestModalTitle')}
          onClose={() => setShowAddRequest(false)}
        >
          <RequestMaintenanceForm
            toggleModal={() => setShowAddRequest(false)}
            onSuccess={handleAddRequestSuccess}
          />
        </ModalContainer>
      )}
    </Page>
  )
}
